/**
 * Adapt the API-03 response into the shape the Indicator Detail screen renders.
 *
 * WHY AN ADAPTER RATHER THAN CHANGING THE PAGE: the page was built against the
 * mockup's `data.js` shape and its rendering logic — status colours, notices,
 * source lines, the revision form — is already reviewed and signed off. Mapping
 * the API onto that shape keeps all of it intact, so switching from mock data to
 * live data changes what is displayed and nothing about how.
 *
 * The mockup vocabulary differs from the API's on purpose:
 *   page  status : 'Yes' | 'Partial' | 'No'      (statusLabel() renders these)
 *   API   status : 'Met' | 'Partially' | 'Not Met'
 *
 * Everything here is defensive. Every field on the CPC side is nullable, the
 * metadata collections are mid-correction (Seong Min, 6 Aug), and a missing
 * field must degrade to an empty section — never to a crash on a screen a
 * government reviewer is looking at.
 */

/** API status vocabulary -> the mockup's internal one. */
const STATUS_TO_PAGE = {
    'Met': 'Yes',
    'Partially': 'Partial',
    'Not Met': 'No',
};

/**
 * display_case -> the mockup's `dataFrom`, which drives itemNotice() and
 * sourceLines().
 *
 *   scored_ai       the AI pipeline produced a country value   -> internal
 *   scored_external an external dataset supplied it            -> external
 *   estimated       a regional/global mean was borrowed        -> missing
 *   no_value        no value at all, and no estimate           -> missing
 *
 * `estimated` and `no_value` BOTH map to the mockup's 'missing' because the
 * mockup had only one empty-score state. They are NOT the same thing, and the
 * difference matters to the reviewer, so `noValue` below keeps them apart —
 * see the comment on it.
 */
const CASE_TO_DATA_FROM = {
    scored_ai: 'internal',
    scored_external: 'external',
    estimated: 'missing',
    no_value: 'missing',
};

/** `internal_external` -> the mockup's `src`. Spaces removed, spelling kept. */
const PROVENANCE_TO_SRC = {
    'Internal': 'Internal',
    'External Survey': 'ExternalSurvey',
    'External Fixed': 'ExternalFixed',
};

/** "CPCI Target 05" -> 5. Returns null when there is no trailing number. */
const numFromId = (id) => {
    const m = String(id || '').match(/(\d+)\s*$/);
    return m ? parseInt(m[1], 10) : null;
};

/**
 * Response options as a LIST, in rubric order.
 *
 * The published format is `N {{option text}}`, blank-line separated:
 *
 *   1 {{No national data protection law exists...}}
 *
 *   2 {{Fragmented or sector-specific provisions exist...}}
 *
 * The number is the rubric score, which is also what `item_checklist_response`
 * is compared against to mark the CURRENT option — so it is parsed out rather
 * than printed, otherwise the screen numbers every line twice.
 *
 * External statistical items have no options at all (the field is `0`), which is
 * correct: there is no government response to give. Those render no options
 * block. The mockup does `it.opts.map(...)`, so this always returns an array.
 */
const toOptions = (raw) => {
    if (Array.isArray(raw)) return raw.map((o) => String(o).trim()).filter(Boolean);

    const s = String(raw === null || raw === undefined ? '' : raw).trim();
    if (!s || s === '0') return [];

    // Preferred: the documented `N {{text}}` form, kept in rubric order.
    const matches = [...s.matchAll(/(\d+)\s*\{\{([\s\S]*?)\}\}/g)];
    if (matches.length) {
        return matches
            .map((m) => ({ n: parseInt(m[1], 10), text: m[2].trim() }))
            .sort((a, b) => a.n - b.n)
            .map((o) => o.text)
            .filter(Boolean);
    }

    // Fallback for any row not yet in that form.
    const lines = s.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    return lines.length > 1 ? lines : [];
};

/** One API item -> one mockup item. */
const adaptItem = (it) => {
    const dataFrom = CASE_TO_DATA_FROM[it.display_case] || 'missing';
    const src = PROVENANCE_TO_SRC[it.provenance] || 'Internal';
    const hasValue = it.value !== null && it.value !== undefined && it.value !== '';
    // Parsed once: `opts` renders it and `cur` measures against it, and the two
    // must not be able to disagree about how many options exist (PBDQ-178).
    const opts = toOptions(it.response_options);

    /**
     * PBDQ-189 — hide the assessment rationale and the supporting-evidence links.
     *
     * Seong Min: "If it is an External Survey and final_score_source is EXT,
     * rationals and evidence should not be displayed."
     *
     * BOTH conditions are required, and that is the whole point. The score of
     * such an item comes straight from the published survey, so the arbiter
     * fields behind it describe DQ's own reconciliation rather than a finding
     * about the country — the reviewer should see the question, the response
     * list and the source, and nothing that implies an adjudication happened.
     *
     * External Survey items scored by AI are a genuinely different case and KEEP
     * both blocks: for AE that is 14 items against the 30 hidden here, so
     * matching on provenance alone would wrongly strip them.
     *
     * Blanked here rather than branched in the page, so the existing
     * `{it.rationale && ...}` and `{it.urls?.length > 0 && ...}` guards do the
     * hiding and there is only one place that decides.
     */
    const hideAdjudication = src === 'ExternalSurvey' && it.score_source === 'EXT';

    /**
     * 1-based index of the selected option, or -1 for "this value is not a
     * selection at all".
     *
     * PBDQ-178 — this single check decides BOTH the CURRENT marker and whether a
     * denominator is printed, so the score and the list can never disagree. A
     * value only counts as a selection when it genuinely falls inside the list:
     * a statistic of 69 against a 4-option list is not option 69.
     */
    const curIndex = (() => {
        if (!hasValue) return -1;
        const n = Number(it.value);
        return Number.isInteger(n) && n >= 1 && n <= opts.length ? n : -1;
    })();

    return {
        code: it.item_id,
        name: it.name || it.item_id,
        src,
        dataFrom,

        /**
         * TRUE when there is no value AND no estimate was made.
         *
         * The mockup had a single empty-score state and its copy says the score
         * "was therefore estimated using the applicable regional reference
         * value". For `estimated` items that is true. For `no_value` items it is
         * FALSE — nothing was published and nothing was borrowed — and telling a
         * government reviewer their item was regionally estimated when it was
         * not is a claim about their country's data that we cannot support.
         *
         * 36 of Albania's 223 items are `no_value`, so this is not an edge case.
         * itemNotice() checks this FIRST, before any dataFrom branch.
         */
        noValue: it.display_case === 'no_value',
        nonScoreCode: it.non_score_code || null,
        nonScoreMeaning: it.non_score_meaning || '',

        /**
         * The score box.
         *
         * `outOf` is the RUBRIC denominator, and only rubric items have one.
         * An externally sourced item carries a statistic in its own unit — a
         * percentage, an index score — not a position on a 0-5 scale, so it must
         * be shown bare. Seong Min's SCR-DTL deck renders exactly that: the
         * external statistical card reads "CURRENT SCORE 72", with no "/ 5".
         *
         * Hardcoding 5 printed things like "21 / 5" for an item whose real value
         * was 21% — a number that cannot be read as anything but wrong, and one
         * a government reviewer would be right to challenge. The page treats
         * outOf 100 as "print the value alone", which is the behaviour wanted
         * for every external value.
         */
        raw: hasValue ? it.value : null,

        /**
         * PBDQ-178 — the denominator is the SIZE OF THIS ITEM'S RESPONSE LIST,
         * and it is printed only when the score is actually a choice from it.
         *
         * Rubrics are not all out of 5. AE `Indicator 080 / cp003` has six
         * options and a score of 4, so a hardcoded 5 printed "4 / 5" under a
         * list numbered 1-6 — Seong Min: "It should be 4/6." Two other items on
         * that same indicator do have five, so the length varies item by item and
         * can only come from the item itself.
         *
         * Keying this on the display case was wrong: External SURVEY items are
         * rubric items too (`cp125` scores 2 from a 4-option list) and were
         * printing a bare "2" beside a four-item list — the very mismatch this
         * ticket is about. Only External FIXED items are raw statistics, and they
         * have no list at all, so `curIndex` is -1 for them.
         *
         * 100 is the page's sentinel for "print the value alone".
         */
        outOf: curIndex > 0 ? opts.length : 100,

        /**
         * `q` and `measures` are MUTUALLY EXCLUSIVE — the screen renders whichever
         * is set, so populating both prints the same paragraph twice.
         *
         *   Internal + External Survey -> `q`        the question, with options
         *   External Fixed             -> `measures` what the statistic measures
         *
         * PBDQ-177 — External SURVEY items show the question and response list
         * too, with the current selection marked. Seong Min: "Since this is an
         * External Survey, the recent selections and scores should be displayed
         * along with the response list for the corresponding item."
         *
         * Only External FIXED stays on `measures`. Those are pure statistics —
         * a percentage from a published index — with no response list to pick
         * from, so a question and options would be inventing a choice that was
         * never offered.
         */
        q: src === 'ExternalFixed' ? '' : (it.question || ''),
        measures: src === 'ExternalFixed' ? (it.question || '') : '',
        opts,

        /**
         * Which option to mark CURRENT — the same check the denominator uses, so
         * a marked option and a printed "x / n" always agree (PBDQ-178).
         */
        cur: curIndex,

        /**
         * PBDQ-175 — for AI-scored items the citation claims are shown as their
         * own block. Seong Min: "Since the Source is AI, the value of
         * 'arbiter_citation_claims' should be displayed."
         *
         * Only for AI. On an external item the score comes from the published
         * source, so there are no AI citation claims to stand behind it.
         */
        /**
         * PBDQ-175 — citation claims, shown for AI-scored items only.
         *
         * On an external item the score comes from the published source, so there
         * are no AI claims standing behind it.
         */
        citationClaims: it.score_source === 'AI' ? (it.citation_claims || '') : '',

        /**
         * Adjudication text shown under the "Assessment rationale" heading.
         *
         * This is `arbiter_determination`, NOT `arbiter_rationale` — Seong Min,
         * 11:35 AM: "The sentence shown here should use 'arbiter_determination'
         * instead of 'arbiter_rationale'. Although it is different from the
         * paragraph title."
         *
         * So the heading intentionally stays "Assessment rationale" while the
         * body carries the determination. `arbiter_determination` states what
         * was found about the country ("Australia has national cybersecurity
         * guidance..."); `arbiter_rationale` explains the tier arithmetic
         * ("Applying the highest-fully-supported-tier rule..."), which is
         * scoring methodology rather than a finding the government can act on.
         *
         * Both remaining fallbacks are kept because every arbiter_* field is
         * nullable on the CPC side, and an empty block is worse than the
         * next-best sentence.
         */
        rationale: hideAdjudication
            ? ''
            : (it.determination || it.rationale || it.citation_claims || ''),
        sourceDetails: [it.data_source, it.organization].filter(Boolean).join(' — '),
        urls: hideAdjudication || !Array.isArray(it.sources) ? [] : it.sources,

        // Used by sourceLines() for the external and estimated cases.
        extTitle: it.data_source || '',
        extOrg: it.organization || '',
        /**
         * What prints after "Estimation basis:" — the NAME of the reference
         * group ("Southern Europe"), which is what Seong Min confirmed should be
         * shown (Q1, 1:22 PM). It was previously the level CODE, which rendered
         * blank. The API resolves the name, so nothing here maps geographies.
         */
        region: it.estimate_basis || '',

        conf: it.confidence || '',

        // Carried through untouched so the UI can show the explicit reason a
        // value is absent rather than an empty box.
        display_case: it.display_case,
        non_score_code: it.non_score_code,
        non_score_meaning: it.non_score_meaning,
        estimate_level: it.estimate_level,
    };
};

/**
 * The whole API-03 payload -> the `ind` object the page expects.
 * Returns null when there is nothing to render.
 */
export const adaptIndicatorDetail = (data) => {
    if (!data || !data.indicator) return null;
    const ind = data.indicator;

    return {
        code: ind.external_id || ind.indicator_id,
        indicator_id: ind.indicator_id,
        name: ind.name || ind.indicator_id,
        question: ind.question || '',

        /**
         * One of the 15 Child indicators. The detail screen must not open for
         * these at all — Seong Min (Q3): "the user cannot access SCR-DTL at all.
         * This is because clicking is disabled in SCR-EXP." Disabling the row is
         * necessary but not sufficient: a typed or bookmarked URL bypasses it.
         */
        isChild: !!data.is_child_indicator,
        status: STATUS_TO_PAGE[ind.status] || 'No',
        conf: ind.confidence || '',
        s: numFromId(ind.stakeholder_id),
        t: numFromId(ind.target_id),
        stakeholder_name: ind.stakeholder_name || '',
        target_name: ind.target_name || '',

        items: (data.items || []).map(adaptItem),

        // FALSE for the 15 Child indicators, which carry no item-level detail by
        // design. The screen must explain that rather than render an empty list
        // that reads as a loading failure.
        item_level_available: data.item_level_available !== false,
        items_by_case: data.items_by_case || {},
        country_code: data.country_code,
        geography: data.geography || {},
    };
};

/**
 * A saved proposal (API-05 shape) -> the Propose Revision FORM shape.
 *
 * These two shapes are almost entirely different names for the same thing: the
 * server stores snake_case with `evidence` and `proposer` nested, the form uses
 * flat camelCase. Of the form's 26 fields only `reasons` and `supervising`
 * happen to share a name.
 *
 * So spreading a server proposal straight onto the form — which is what the
 * edit path used to do — left 24 of 26 fields BLANK. A reviewer reopening their
 * own saved revision saw an empty form and would reasonably conclude their work
 * had been lost, or silently re-save over it with nothing in it.
 *
 * TOLERATES BOTH SHAPES. Immediately after a save the local state holds the
 * FORM object, and only after a reload does the server shape arrive; the edit
 * path sees one or the other depending on timing. Detected by looking for a
 * form-only key rather than by guessing from context.
 *
 * @param {object} rev  a server proposal, or an already-form-shaped object
 * @returns {object|null} form fields to merge over the blank form
 */
export const adaptProposalToForm = (rev) => {
    if (!rev || typeof rev !== 'object') return null;

    // Already the form shape (freshly saved, not yet round-tripped).
    const looksLikeForm = rev.evTitle !== undefined
        || rev.proposedRubric !== undefined
        || rev.pName !== undefined;
    if (looksLikeForm) return rev;

    const ev = rev.evidence || {};
    const pr = rev.proposer || {};
    const s = (v) => (v === null || v === undefined ? '' : String(v));

    return {
        noScoreChange: !!rev.no_score_change,
        // Kept as a number (or null) — the rubric radio compares by value.
        proposedRubric: rev.proposed_rubric === null || rev.proposed_rubric === undefined
            ? null
            : Number(rev.proposed_rubric),
        propValue: s(rev.proposed_value),
        propUnit: s(rev.proposed_unit),
        propYear: s(rev.proposed_year),
        propCoverage: s(rev.proposed_coverage),

        reasons: Array.isArray(rev.reasons) ? rev.reasons : [],
        otherReason: s(rev.other_reason),

        evTitle: s(ev.title),
        evIssuer: s(ev.issuer),
        evDate: s(ev.date),
        evJurisdiction: s(ev.jurisdiction),
        evLocation: s(ev.location),
        evExcerpt: s(ev.excerpt),
        evUrl: s(ev.url),

        legalStatus: s(rev.legal_status),
        implStatus: s(rev.implementation_status),

        pName: s(pr.name),
        pOrg: s(pr.organization),
        pDept: s(pr.department),
        pRole: s(pr.role),
        pEmail: s(pr.email),

        focalPoint: s(rev.focal_point),
        supervising: s(rev.supervising),
        techContact: s(rev.technical_contact),

        // Decides whether the form asks for a rubric level or a value+unit+year.
        // Falling back to the item's own kind would be wrong here: what was
        // SAVED is what must be edited.
        itemKind: rev.item_kind === 'value' ? 'value' : 'rubric',
    };
};

export default adaptIndicatorDetail;
